import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import * as editableDocument from 'utils/model/editableDocument';
import { showError } from 'actions/actionCreators/snackbarActions';
import { useTranslation } from 'react-i18next';
import { updateWarranties, loadWarranties, deleteWarranty, deleteAllWarranties, getWarrantyIssuer } from '../actionCreators';
import { loadWarrantyTypes, loadPartnerRoles } from 'actions/actionCreators/dictionariesActions';
import { collection } from 'utils/immutable';
import { getWarranties, getWarrantyFlags, getDefaultWarrantyData } from '../selectors';
import { loadCurrencies } from 'actions/actionCreators/dictionariesActions';
import { loadOfferAssetsHeader } from 'features/offer/edit/assets/asset/actionCreators';
import { loadGuarantors } from 'features/document/edit/documents/actionCreators';
import { loadPartnerTypes } from 'features/offer/edit/client/form/actionCreators';
import { isMandatory, dateIsGreaterThan, dateIsLessThan, isPositive, isValidDateFormat } from 'utils/simple-validations';
import flow from 'lodash.flow';
import { getRandomNegativeInt } from 'utils/functions';
import { withParams } from 'providers/ParamsConsumer';
import { when } from 'utils/fn-combinators';
import WarrantyDataContent from './WarrantyDataContent';

const getItemValidationRules = (warranty, fSWarrantyDetailsVisibility) => {
  return {
    dictionaryWarrantyTypeId: [when(!fSWarrantyDetailsVisibility, isMandatory())],
    warrantyTypeId: [when(fSWarrantyDetailsVisibility, isMandatory())],
    warrantyItem: [when(fSWarrantyDetailsVisibility, isMandatory())],
    issuerTypeId: [isMandatory()],
    issuer: [isMandatory()],
    qtty: [isMandatory(), isPositive()],
    amount: [isPositive()],
    validFrom: [
      when(Boolean(warranty?.document?.validFrom) && Boolean(warranty?.document?.validTo), dateIsLessThan(warranty?.document?.validTo)),
      isValidDateFormat()
    ],
    validTo: [
      when(
        Boolean(warranty?.document?.validFrom) && Boolean(warranty?.document?.validTo),
        dateIsGreaterThan(warranty?.document?.validFrom)
      ),
      isValidDateFormat()
    ]
  };
};

const WarrantyDataContentContainer = ({
  documentId,
  siteId,
  folderOffer,
  warranties,
  actions,
  loaded,
  warrantyTypes,
  partnerRoles,
  defaultWarrantyData,
  params
}) => {
  const [localWarranties, setLocalWarranties] = useState([]);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    actions.loadWarrantyTypes();
    actions.loadCurrencies();
    actions.loadOfferAssetsHeader(documentId, siteId);
    actions.loadPartnerTypes();
    actions.loadPartnerRoles();
    actions.loadGuarantors(documentId, siteId);
  }, [actions, documentId, siteId]);

  useEffect(() => {
    if (loaded) return;
    actions.loadWarranties(documentId, siteId);
  }, [documentId, siteId, actions, loaded]);

  useEffect(() => {
    if (!loaded) return;
    setLocalWarranties(warranties?.map(editableDocument.create) || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const changeWarrantyIssuer = useCallback(
    async (idx, value) => {
      const issuer = await actions.getWarrantyIssuer(documentId, siteId, value);

      let _flow = null;
      if (issuer?.issuerId)
        _flow = flow(
          editableDocument.update('issuerTypeId', value, null),
          editableDocument.update('issuer', { issuerId: issuer.issuerId, issuerName: issuer.issuerName }, null),
          editableDocument.validate(getItemValidationRules(localWarranties[idx], params.fSWarrantyDetailsVisibility), true)
        );
      else if (partnerRoles.find(x => x.partnerRoleId === value && ['Dealer', 'MBTB', 'MBRO'].includes(x.name))) {
        _flow = flow(
          editableDocument.update('issuerTypeId', value, null),
          editableDocument.update('issuer', defaultWarrantyData.issuer, null),
          editableDocument.validate(getItemValidationRules(localWarranties[idx], params.fSWarrantyDetailsVisibility), true)
        );
      } else
        _flow = flow(
          editableDocument.update('issuerTypeId', value, null),
          editableDocument.validate(getItemValidationRules(localWarranties[idx], params.fSWarrantyDetailsVisibility), true)
        );

      setLocalWarranties(prevState => collection.update(idx, _flow, prevState));
    },
    [actions, defaultWarrantyData.issuer, documentId, localWarranties, partnerRoles, siteId, params.fSWarrantyDetailsVisibility]
  );

  const changeWarrantyType = useCallback(
    (idx, value) => {
      const warrantyType = warrantyTypes.find(x => x.warrantyTypeId === value);
      setLocalWarranties(prevState =>
        collection.update(
          idx,
          flow(
            editableDocument.update('warrantyTypeId', value, null),
            editableDocument.update('enableGuarantor', warrantyType.enableGuarantor, null),
            editableDocument.validate(getItemValidationRules(localWarranties[idx], params.fSWarrantyDetailsVisibility), true)
          ),
          prevState
        )
      );
    },
    [localWarranties, warrantyTypes, params.fSWarrantyDetailsVisibility]
  );

  const handlePropertyChange = useCallback(
    (idx, prop, value) => {
      switch (prop) {
        case 'issuerTypeId': {
          changeWarrantyIssuer(idx, value);
          break;
        }
        case 'warrantyTypeId': {
          changeWarrantyType(idx, value);
          break;
        }
        case 'dictionaryWarrantyTypeId': {
          const warrantyType = warrantyTypes.find(x => x.warrantyTypeId === value);
          setLocalWarranties(prevState =>
            collection.update(
              idx,
              flow(
                editableDocument.update('dictionaryWarrantyTypeId', warrantyType.warrantyTypeId, null),
                editableDocument.update('dictionaryWarrantyTypeName', warrantyType.warrantyTypeName, null),
                editableDocument.update('enableGuarantor', warrantyType.enableGuarantor, null),
                editableDocument.validate(getItemValidationRules(localWarranties[idx], params.fSWarrantyDetailsVisibility), true)
              ),
              prevState
            )
          );
          break;
        }
        case 'validFrom':
        case 'validTo': {
          setLocalWarranties(prevState =>
            collection.update(
              idx,
              flow(
                editableDocument.update(prop, value, null),
                editableDocument.validate(
                  getItemValidationRules(
                    {
                      ...localWarranties[idx],
                      document: { ...localWarranties[idx].document, [prop]: value }
                    },
                    params.fSWarrantyDetailsVisibility
                  ),
                  true
                )
              ),
              prevState
            )
          );
          break;
        }
        default:
          setLocalWarranties(prevState =>
            collection.update(
              idx,
              flow(
                editableDocument.update(prop, value, null),
                editableDocument.validate(getItemValidationRules(localWarranties[idx], params.fSWarrantyDetailsVisibility), true)
              ),
              prevState
            )
          );
          break;
      }
    },
    [changeWarrantyIssuer, changeWarrantyType, localWarranties, warrantyTypes, params.fSWarrantyDetailsVisibility]
  );

  const onSaveWarranties = useCallback(() => {
    const validationList = localWarranties.map(item => {
      return editableDocument.validate(getItemValidationRules(item, params.fSWarrantyDetailsVisibility), false, item);
    });

    if (validationList.some(x => !x.validationInfo.isValid)) {
      setLocalWarranties(validationList);
      dispatch(showError(t('Document.Warranty.ErrorMessage')));
      return;
    }
    setSaving(true);

    try {
      actions.updateWarranties(
        documentId,
        siteId,
        localWarranties.map(x => x.document)
      );
      setSaving(false);
    } catch (error) {
      setSaving(false);
      throw error;
    }
  }, [actions, dispatch, documentId, localWarranties, siteId, t, params.fSWarrantyDetailsVisibility]);

  const onDeleteAllWarranties = useCallback(() => {
    const warrantiesToBeDeleted = localWarranties;
    setLocalWarranties([]);

    actions.deleteAllWarranties(documentId, siteId, warrantiesToBeDeleted);
  }, [actions, documentId, localWarranties, siteId]);

  const onDeleteWarranty = useCallback(
    warranty => {
      const warrantyIndex = localWarranties.findIndex(x => x.document.warrantyId === warranty.warrantyId);

      if (warrantyIndex === -1) return;

      setLocalWarranties(collection.remove(warrantyIndex, localWarranties));

      actions.deleteWarranty(documentId, siteId, warranty.warrantyId);
    },
    [actions, documentId, localWarranties, siteId]
  );

  const onAddNewWarranty = useCallback(() => {
    const warrantyKeys = [
      'amount',
      'currencyCode',
      'currencyId',
      'currencyName',
      'documentDetailId',
      'documentDetailSiteId',
      'documentId',
      'freeTxt',
      'guaranteeNumber',
      'warrantyItem',
      'guarantor',
      'itemName',
      'siteId',
      'validFrom',
      'validTo',
      'warrantyId',
      'warrantyTypeCode',
      'warrantyTypeId',
      'warrantyTypeName',
      'issuerTypeId',
      'issuer',
      'qtty',
      'dictionaryWarrantyTypeId',
      'dictionaryWarrantyTypeName'
    ];

    const newWarranty = warrantyKeys.reduce((accumulator, current) => {
      if (current === 'amount') accumulator[current] = 0;
      else accumulator[current] = '';
      return accumulator;
    }, {});

    newWarranty.documentId = documentId;
    newWarranty.siteId = siteId;
    newWarranty.warrantyId = getRandomNegativeInt(10000, 2147483647);
    newWarranty.currencyId = defaultWarrantyData?.currencyId;
    newWarranty.currencyCode = defaultWarrantyData?.currencyCode;
    setLocalWarranties(collection.insert(localWarranties.length, editableDocument.create(newWarranty), localWarranties));

    handlePropertyChange(localWarranties.length, 'documentDetail', defaultWarrantyData.documentDetail);
  }, [
    defaultWarrantyData?.currencyCode,
    defaultWarrantyData?.currencyId,
    defaultWarrantyData.documentDetail,
    documentId,
    handlePropertyChange,
    localWarranties,
    siteId
  ]);

  return localWarranties ? (
    <WarrantyDataContent
      warranties={localWarranties}
      onPropertyChange={handlePropertyChange}
      saving={saving}
      onAddNewWarranty={onAddNewWarranty}
      onSaveWarranties={onSaveWarranties}
      onDeleteWarranty={onDeleteWarranty}
      onDeleteAllWarranties={onDeleteAllWarranties}
      folderOffer={folderOffer}
    />
  ) : (
    <></>
  );
};

WarrantyDataContentContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  warranties: PropTypes.array,
  loaded: PropTypes.bool.isRequired,
  warrantyTypes: PropTypes.array.isRequired,
  partnerRoles: PropTypes.array.isRequired,
  defaultWarrantyData: PropTypes.object,
  params: PropTypes.object.isRequired,
  folderOffer: PropTypes.object
};

function mapStateToProps(state, route) {
  const key = `${route.documentId}-${route.siteId}`;
  const { loaded } = getWarrantyFlags(state.document.warrantyData.warranty, state.entities.warranties, key);

  return {
    warranties: getWarranties(state.document.warrantyData.warranty, state.entities.warranties, key),
    loaded: loaded,
    warrantyTypes: state.dictionaries.warrantyTypes.isLoaded ? state.dictionaries.warrantyTypes.items : [],
    partnerRoles: state.dictionaries.partnerRoles.isLoaded ? state.dictionaries.partnerRoles.items : [],
    defaultWarrantyData: getDefaultWarrantyData(state, route.documentId)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateWarranties,
        loadWarranties,
        loadWarrantyTypes,
        loadCurrencies,
        loadGuarantors,
        loadOfferAssetsHeader,
        loadPartnerTypes,
        deleteWarranty,
        deleteAllWarranties,
        loadPartnerRoles,
        getWarrantyIssuer
      },
      dispatch
    )
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(withParams(WarrantyDataContentContainer));
